import { DisplayState } from '../../../api/gen/model/displayState';

/**
 * The `ComponentWidget` layers the edge-app actually knows how to draw.
 *
 * `componentType` is stored as free text on the layer and the backend accepts any string, so a typo
 * used to be invisible: the layer saved fine, the display rendered an empty box, and the first
 * evidence was a parker at a barrier with nothing to scan. This catalogue is what the editor offers
 * instead of a bare text field.
 *
 * **Keep in sync with `QR_WIDGETS` in `edge-app/.../layer.component.ts`.** That map is the real
 * renderer; this one only decides what the operator is offered and warned about.
 */
export interface DisplayWidgetDefinition {
    /** Exact value stored on `LayerDefinitionDto.componentType`. Case-insensitive at render time. */
    componentType: string;
    label: string;
    /** What the operator sees on the screen when this layer renders. */
    description: string;
    /** The LiveData key the widget reads out of `componentData`. */
    liveDataKey: string;
    /**
     * The display states whose LiveData carries {@link liveDataKey}. On any other state the widget
     * renders nothing — there is no URL to encode — which is the single most common way to build a
     * QR layer that silently never appears.
     */
    states: DisplayState[];
    /** True when the layer's Position needs an explicit width/height to size the output sensibly. */
    needsSize: boolean;
}

export const DISPLAY_WIDGETS: DisplayWidgetDefinition[] = [
    {
        componentType: 'PaymentQrWidget',
        label: 'Payment QR',
        description:
            'QR code the parker scans to pay for the stay they are exiting. Opens the payment app’s ' +
            'settlement page for this parking process.',
        liveDataKey: 'exit.payment_qr_url',
        // PaymentRequiredQr only. PaymentRequired sounds like the right state and is not: the engine
        // (ExitDisplayStateMapper) routes ExitPaymentMethod.StripeQr — the only method that ever sets
        // PaymentQrUrl — to PaymentRequiredQr. CardOrNfc (the column-prompt case) maps to
        // PaymentRequired and never carries this key.
        states: [DisplayState.PaymentRequiredQr],
        needsSize: true,
    },
    {
        componentType: 'ReceiptQrWidget',
        label: 'Receipt QR',
        description:
            'QR code linking to the fiscal receipt for a payment that has just completed.',
        liveDataKey: 'exit.receipt_url',
        states: [DisplayState.PaymentDone],
        needsSize: true,
    },
];

/** Catalogue lookup, case-insensitive because the renderer matches that way too. */
export function findWidget(componentType: string | null | undefined): DisplayWidgetDefinition | undefined {
    const key = (componentType ?? '').trim().toLowerCase();
    if (!key) return undefined;
    return DISPLAY_WIDGETS.find(w => w.componentType.toLowerCase() === key);
}
